<template>
    <div
        image
        question
        :id="`question-${questionData.number}`"
        @keyup.enter="submitQuestion"
        ref="main"
    >
        <questionheader :questionData="questionData" :showImage="false" />
        <div data-question-main>
            <div data-image-area>
                <zoom-image
                    v-if="mainImage !== undefined"
                    :src="imageURL"
                    :id="`question-${questionData.number}_media-image`"
                    :alt="mainImage.alt"
                    type="Image"
                    data-main-image
                    ref="main-image"
                />
                <template v-if="imageIsLoaded">
                    <div
                        v-for="answer in questionData.answers"
                        :key="`area-${answer.id}`"
                        :id="`question-${questionData.number}_area-${answer.number}`"
                        data-option
                        :data-is-hovered="answer.isHovered"
                        :data-is-checked="answer.isChecked"
                        :answer-is-open="answer.isOpen"
                        @focus="answerIsClicked(answer)"
                        @keydown.space.prevent="answerIsClicked(answer)"
                    >
                        <input
                            :type="answer.htmlInputType"
                            :id="`question-${questionData.number}_answer-checkbox-${answer.number}`"
                            :name="`${questionData.id}-answers`"
                            @click="answerIsClicked(answer)"
                            data-only-screenreader
                            :checked="answer.isChecked"
                            :tabindex="answer.isDisabled || !editingAllowed ? -1 : 0"
                        />
                        <label
                            v-for="(answerArea, index) in answer.answerAreas"
                            data-area
                            :data-show-selected="answerArea.showSelected"
                            :key="`answer-${answer.id}-${index}`"
                            :style="{
                                height: `${(answerArea.height / mainImageDimensions.y) * 100}%`,
                                width: `${(answerArea.width / mainImageDimensions.x) * 100}%`,
                                left: `${(answerArea.offsetLeft / mainImageDimensions.x) * 100}%`,
                                top: `${(answerArea.offsetTop / mainImageDimensions.y) * 100}%`
                            }"
                            :disabled="answer.isDisabled || !editingAllowed"
                            :id="`question-${questionData.number}_answer-${answer.number}-${index}`"
                            :for="`question-${questionData.number}_answer-checkbox-${answer.number}`"
                            @mouseenter="areaMouseEnter(answer)"
                            @mouseleave="areaMouseLeave(answer)"
                        >
                            <strong :aria-hidden="index > 0" v-html="DOMPurify.sanitize(answer.label)"></strong>
                        </label>
                    </div>
                </template>
            </div>
        </div>
        <questionfooter :questionData="questionData" />
        <validationpopup :questionValidation="questionValidation" />
    </div>
</template>

<script>
import mixins from "@/global/mixins";
import zoomImage from "@/components/ZoomImage";

export default {
    name: `image-question`,
    inject: ['DOMPurify'],
    components: {
        zoomImage
    },
    mixins: [mixins.defaultQuestion],
    data: function () {
        return {
            imageIsLoaded: false,
            mainImageDimensions: {
                x: 0,
                y: 0,
            },
        };
    },
    computed: {
        answersGiven() {
            return this.questionData.answers.filter((answer) => answer.isChecked).length;
        },
        editingAllowed: function () {
            return this.get.editingAllowed(this.questionData) || this.state.global.showLoader;
        },
        imageURL: function () {
            return `/v1/${this.mainImage.url}`;
        },
        mainImage: function () {
            return this.questionData.media[0];
        },
        checkedExclusiveAnswer: function () {
            return this.questionData.answers.find((answer) => answer.isChecked && answer.isExclusive);
        },
    },
    methods: {
        answerIsClicked: function (answer) {
            // v-model doesn't like radio buttons
            if (this.questionData.type === `ImageMulti`) {
                answer.isChecked = !answer.isChecked;
            } else if (this.questionData.type === `ImageSingle`) {
                this.questionData.answers.forEach((answer) => {
                    answer.isChecked = false;
                });

                answer.isChecked = true;
            }

            // If an answer that is exclusive has been clicked, disable all remaining options
            if (answer.isExclusive) {
                this.checkForExclusiveAnswer(answer);
            }
        },
        areaMouseEnter: function (answer) {
            answer.isHovered = true;
            answer.isOpen = true; // isOpen to force a repaint of the template :/
        },
        areaMouseLeave: function (answer) {
            answer.isHovered = false;
            answer.isOpen = false; // isOpen to force a repaint of the template :/
        },
        checkForExclusiveAnswer: function (answer) {
            this.questionData.answers
                .filter((a) => a.id !== answer.id)
                .forEach((a) => {
                    if (answer.isChecked) {
                        a.isChecked = false;
                        a.isDisabled = true;
                    } else {
                        a.isDisabled = false;
                    }
                });
        },
        getImageDimensions: function () {
            if (this.mainImage === undefined) return;

            let img = new Image();
            let _this = this;

            img.onload = () => {
                _this.imageIsLoaded = true;
                _this.mainImageDimensions.x = img.width;
                _this.mainImageDimensions.y = img.height;
            };

            img.src = this.imageURL;
        },
    },
    mounted: function () {
        this.getImageDimensions();
    },
    created: function () {
        // If there is an exclusive and checked answer at pageload, we should disable the other answers
        if (this.checkedExclusiveAnswer) this.checkForExclusiveAnswer(this.checkedExclusiveAnswer);
    },
};
</script>

<style scoped>
@import "./../styles/variables.css";

[data-image-area] {
    position: relative;
    margin: 0;

    img {
        margin: 0;
    }
}

[data-question-main] {
    display: flex;
    align-items: center;
}

[data-option] {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: none;

    label {
        pointer-events: all;
        position: absolute;
        overflow: hidden;
    }

    &:focus {
        outline: 0;
    }

    &[tabindex="-1"] label {
        pointer-events: none;
    }
}

input[type="radio"],
input[type="checkbox"] {
    &:checked ~ label {
        border-color: var(--primary-color);
        background: var(--primary-color);
        border: 2px solid #333;
        opacity: 0.8;
    }

    &:focus ~ label {
        box-shadow: 0 0 5px black;
        background: var(--primary-color);
        opacity: 0.6;
    }

    &:focus:checked ~ label {
        opacity: 0.8;
    }
}

[data-area] {
    border-radius: 5px;
    transition: 0.4s background;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;

    strong {
        opacity: 0;
        mix-blend-mode: revert;
        text-align: center;
        font-family: var(--AnswerStyling-font-family-public-name);
    }

    &[disabled] {
        border-color: transparent !important; /* Override other styling. This item being disabled should always be clear to the user*/
        pointer-events: none;
    }

    &[data-show-selected] {
        background: #ffffff66;
        border: 2px #777 dotted;

        strong {
            opacity: 1;
        }
    }
}

[data-is-checked],
[data-is-hovered] {
    strong {
        opacity: 1;
    }
}

[data-is-hovered] {
    [data-area] {
        background: var(--primary-color);
        border: 2px #333 dotted;
        opacity: 0.8;
    }
}

@media only screen and (max-width: 550px) {
    body [question] div[data-question-main] {
        padding: 15px 25px;
    }
}
</style>